import { useEffect, useState } from "react";
import { X, Rocket, Users, Calendar, ClipboardCheck, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sparkles from "./Sparkles";
import ParticleBurst from "./ParticleBurst";

/**
 * WelcomeModal — shown once after a first login from the seller flow.
 *
 * Trigger: localStorage key `maidly_first_login_welcome` is set to a JSON
 * payload like `{"tier":"free","ts":1700000000}` after a successful login
 * with `?from=trial` or `?from=purchase` query params.
 *
 * On close, clears the flag so the modal is never shown again.
 */
const WelcomeModal = ({ user }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [showBurst, setShowBurst] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("maidly_first_login_welcome");
      if (raw) {
        const data = JSON.parse(raw);
        setPayload(data);
        // Small delay so the dashboard renders first, then the welcome surprises the user
        const t = setTimeout(() => {
          setOpen(true);
          setShowBurst(true);
        }, 350);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // bad payload, ignore
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    try {
      localStorage.removeItem("maidly_first_login_welcome");
    } catch (e) { /* ignore */ }
  };

  const handleAction = (path) => {
    handleClose();
    navigate(path);
  };

  if (!open || !payload) return null;

  const tier = payload.tier || "free";
  const tierLabel = tier === "pro" ? "Pro · 1 year" : "Free Trial · 15 days";
  const userName = user?.name || user?.email?.split("@")[0] || "there";

  return (
    <>
      {showBurst && (
        <ParticleBurst
          count={90}
          duration={2200}
          onDone={() => setShowBurst(false)}
        />
      )}

      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.65)",
          backdropFilter: "blur(8px)",
          zIndex: 9998,
          animation: "welcome-fade-in 0.25s ease-out",
        }}
      />

      <style>{`
        @keyframes welcome-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes welcome-pop-in {
          0% { transform: translate(-50%, -50%) scale(0.85); opacity: 0; }
          70% { transform: translate(-50%, -50%) scale(1.03); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
      `}</style>

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "92%",
          maxWidth: "560px",
          background: "#ffffff",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 25px 80px -10px rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
          animation: "welcome-pop-in 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Hero with sparkles */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)",
            color: "#fff",
            padding: "2.5rem 1.5rem 2rem",
            textAlign: "center",
          }}
        >
          <Sparkles count={28} color="rgba(255,255,255,0.9)" minSize={6} maxSize={16} />
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: "0.75rem",
              right: "0.75rem",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <X size={18} />
          </button>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                background: "rgba(255,255,255,0.18)",
                padding: "0.3rem 0.85rem",
                borderRadius: "999px",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
                marginBottom: "1rem",
                fontWeight: 600,
              }}
            >
              <Rocket size={14} />
              {tierLabel}
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: "1.85rem",
                fontWeight: 800,
                letterSpacing: "-0.5px",
              }}
            >
              Welcome to Maidly, {userName}! 🎉
            </h2>
            <p
              style={{
                margin: "0.5rem 0 0",
                fontSize: "0.95rem",
                opacity: 0.92,
                maxWidth: "420px",
                marginLeft: "auto",
                marginRight: "auto",
                lineHeight: 1.5,
              }}
            >
              Your account is all set up. Let's get your cleaning business running smoothly.
            </p>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ padding: "1.5rem", background: "#fff" }}>
          <p
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "1px",
              color: "#1e40af",
              margin: "0 0 0.85rem",
            }}
          >
            QUICK START
          </p>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            <button
              onClick={() => handleAction("/customers")}
              style={quickActionStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
            >
              <span style={iconWrapStyle("#dbeafe", "#1e40af")}><Users size={18} /></span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <strong style={{ display: "block", color: "#0f172a" }}>Add your first customer</strong>
                <span style={{ fontSize: "0.8rem", color: "#64748b" }}>Build your client list</span>
              </span>
              <ArrowRight size={16} color="#94a3b8" />
            </button>
            <button
              onClick={() => handleAction("/scheduling")}
              style={quickActionStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
            >
              <span style={iconWrapStyle("#dcfce7", "#15803d")}><Calendar size={18} /></span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <strong style={{ display: "block", color: "#0f172a" }}>Schedule a cleaning</strong>
                <span style={{ fontSize: "0.8rem", color: "#64748b" }}>Plan your week</span>
              </span>
              <ArrowRight size={16} color="#94a3b8" />
            </button>
            <button
              onClick={() => handleAction("/inspections")}
              style={quickActionStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
            >
              <span style={iconWrapStyle("#fef3c7", "#b45309")}><ClipboardCheck size={18} /></span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <strong style={{ display: "block", color: "#0f172a" }}>Run an inspection</strong>
                <span style={{ fontSize: "0.8rem", color: "#64748b" }}>With photos & signatures</span>
              </span>
              <ArrowRight size={16} color="#94a3b8" />
            </button>
          </div>

          <button
            onClick={handleClose}
            style={{
              marginTop: "1.25rem",
              width: "100%",
              padding: "0.85rem",
              background: "linear-gradient(135deg, #0073ea 0%, #005bb5 100%)",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.95rem",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,115,234,0.3)",
            }}
          >
            Take me to my dashboard
          </button>
          <p
            style={{
              margin: "0.85rem 0 0",
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#94a3b8",
            }}
          >
            💡 Don't forget to change your temporary password in <strong>Settings → Security</strong>
          </p>
        </div>
      </div>
    </>
  );
};

const quickActionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.85rem",
  padding: "0.85rem 1rem",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  cursor: "pointer",
  transition: "background 0.15s ease",
  width: "100%",
  font: "inherit",
};

const iconWrapStyle = (bg, color) => ({
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: bg,
  color,
  borderRadius: "8px",
  flexShrink: 0,
});

export default WelcomeModal;
